/**
 * HUB-E — Données stock pour onglet Centre Activité (mouvements + alertes + stats).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  classifyAlerts,
  computeMagasinAlertCounts,
  computeTopActive,
  computeTopSold,
  matchesArticleSearch,
  matchesMovementSearch,
} from '../../utils/stock/stockDisplay';

export default function useActiviteStock(api, filters, { enabled = true } = {}) {
  const [movements, setMovements] = useState([]);
  const [articles, setArticles] = useState([]);
  const [movStats, setMovStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [movementTypeFilter, setMovementTypeFilter] = useState('all');

  const fetchAll = useCallback(async () => {
    if (!api || !enabled) return;
    setLoading(true);
    setError(null);
    try {
      const movParams = new URLSearchParams({ page: '1', limit: '80' });
      if (filters.magasin_id) movParams.append('magasin_id', filters.magasin_id);
      if (filters.date_debut && filters.date_fin) {
        movParams.append('date_debut', filters.date_debut);
        movParams.append('date_fin', filters.date_fin);
      }

      const stockParams = new URLSearchParams();
      if (filters.magasin_id) stockParams.append('magasin_id', filters.magasin_id);
      if (filters.search) stockParams.append('q', filters.search);

      const statsParams = {};
      if (filters.date_debut && filters.date_fin) {
        statsParams.date_debut = filters.date_debut;
        statsParams.date_fin = filters.date_fin;
      }

      const stockQs = stockParams.toString();
      const [movRes, stockRes, statsRes] = await Promise.all([
        api.get(`/stock-movements?${movParams.toString()}`),
        api.get(stockQs ? `/stock?${stockQs}` : '/stock'),
        api.get('/stock-movements/stats', { params: statsParams }),
      ]);

      setMovements(movRes.data?.movements || []);
      setArticles(stockRes.data || []);
      setMovStats(statsRes.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Erreur chargement stock');
      setMovements([]);
      setArticles([]);
      setMovStats(null);
    } finally {
      setLoading(false);
    }
  }, [api, enabled, filters]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const refToArticle = useMemo(() => {
    const map = {};
    articles.forEach((a) => {
      if (a.reference) map[a.reference] = a;
    });
    return map;
  }, [articles]);

  const enrichMovement = useCallback((m) => ({
    ...m,
    article_id: refToArticle[m.reference]?.id,
  }), [refToArticle]);

  const filteredMovements = useMemo(() => {
    let list = movements.filter((m) => matchesMovementSearch(m, filters.search));
    if (movementTypeFilter === 'entree') {
      list = list.filter((m) => m.type_mouvement === 'entree');
    } else if (movementTypeFilter === 'sortie') {
      list = list.filter((m) => m.type_mouvement === 'sortie' && !['ajustement_inventaire', 'correction_inventaire'].includes(m.motif));
    } else if (movementTypeFilter === 'ajustement') {
      list = list.filter((m) => ['ajustement_inventaire', 'correction_inventaire'].includes(m.motif));
    } else if (movementTypeFilter === 'transfert') {
      list = list.filter((m) => m.type_mouvement === 'transfert');
    }
    return list.map(enrichMovement);
  }, [movements, filters.search, movementTypeFilter, enrichMovement]);

  const transfers = useMemo(
    () => filteredMovements.filter((m) => m.type_mouvement === 'transfert'),
    [filteredMovements],
  );

  const recentMovements = useMemo(
    () => filteredMovements.filter((m) => m.type_mouvement !== 'transfert').slice(0, 20),
    [filteredMovements],
  );

  const filteredArticles = useMemo(
    () => articles.filter((a) => matchesArticleSearch(a, filters.search)),
    [articles, filters.search],
  );

  const { ruptures, faible } = useMemo(
    () => classifyAlerts(filteredArticles),
    [filteredArticles],
  );

  const magasinCounts = useMemo(
    () => computeMagasinAlertCounts(filteredArticles),
    [filteredArticles],
  );

  const topSold = useMemo(
    () => computeTopSold(filteredMovements, 5).map((t) => ({
      ...t,
      id: refToArticle[t.reference]?.id,
    })),
    [filteredMovements, refToArticle],
  );
  const topActive = useMemo(
    () => computeTopActive(filteredMovements, 5).map((t) => ({
      ...t,
      id: refToArticle[t.reference]?.id,
    })),
    [filteredMovements, refToArticle],
  );

  const kpis = useMemo(() => ({
    ruptures: ruptures.length,
    faible: faible.length,
    mouvements: filteredMovements.length,
    transferts: transfers.length,
    entrees: movStats?.total_entrees ?? 0,
    sorties: movStats?.total_sorties ?? 0,
    articles: filteredArticles.length,
  }), [ruptures, faible, filteredMovements, transfers, movStats, filteredArticles]);

  return {
    movements: filteredMovements,
    recentMovements,
    transfers,
    articles: filteredArticles,
    ruptures,
    faible,
    magasinCounts,
    topSold,
    topActive,
    kpis,
    loading,
    error,
    movementTypeFilter,
    setMovementTypeFilter,
    refresh: fetchAll,
  };
}
